import type { ReactNode } from 'react';
import { AlertTriangle, CloudOff, Loader2, RefreshCw, WifiOff } from 'lucide-react';
import { cn } from '../cn';
import { Button } from './forms';

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={cn('animate-[dd-spin_0.8s_linear_infinite] text-brand', className)} aria-label="loading" />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-[dd-pulse_1.4s_ease-in-out_infinite] rounded-lg bg-neutral-200', className)} aria-hidden />;
}

export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4" aria-busy="true" aria-label="loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-card">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-4 w-14" />
        </div>
      ))}
    </div>
  );
}

/** "First empty" and "filter empty" states (Bible 13.x: six states). */
export function EmptyState({ icon, title, body, action, className }: { icon?: ReactNode; title: string; body?: string; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center px-8 py-14 text-center', className)}>
      {icon && <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-light text-brand">{icon}</div>}
      <h3 className="text-lg font-bold text-ink">{title}</h3>
      {body && <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-ink-3">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Server error with retry and the error code (Bible: server error state shows the code). */
export function ErrorState({ message, code, onRetry, retryLabel = 'আবার চেষ্টা করুন' }: { message: string; code?: string; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div role="alert" className="flex flex-col items-center px-8 py-12 text-center">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-bad-bg text-bad"><AlertTriangle size={26} /></div>
      <p className="text-base font-semibold text-ink">{message}</p>
      {code && <p className="mt-1 text-xs text-ink-3">কোড: {code}</p>}
      {onRetry && <Button variant="outline" className="mt-4" icon={<RefreshCw size={16} />} onClick={onRetry}>{retryLabel}</Button>}
    </div>
  );
}

type Tone = 'info' | 'warn' | 'bad' | 'ok';
const TONE: Record<Tone, string> = { info: 'bg-brand-50 text-brand-dark border-brand-light', warn: 'bg-warn-bg text-warn border-amber-200', bad: 'bg-bad-bg text-bad border-red-200', ok: 'bg-ok-bg text-ok border-green-200' };

export function Banner({ tone = 'info', children, action, className }: { tone?: Tone; children: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm', TONE[tone], className)}>
      <div className="min-w-0 flex-1">{children}</div>
      {action}
    </div>
  );
}

/** Offline state: a thin yellow strip under the Top Bar (Bible: offline = yellow strip). */
export function OfflineStrip({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 bg-amber-300 px-3 py-1 text-xs font-semibold text-amber-950">
      <WifiOff size={14} />
      {label}
    </div>
  );
}

export function SyncProblem({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-bad"><CloudOff size={16} /><span className="sr-only">{label}</span></span>
  );
}

export function Badge({ children, tone = 'neutral', className, style }: { children: ReactNode; tone?: Tone | 'neutral' | 'brand'; className?: string; style?: React.CSSProperties }) {
  const t = { neutral: 'bg-neutral-100 text-ink-2', brand: 'bg-brand-light text-brand-dark', info: 'bg-brand-50 text-brand-dark', warn: 'bg-warn-bg text-warn', bad: 'bg-bad-bg text-bad', ok: 'bg-ok-bg text-ok' }[tone];
  return <span style={style} className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', t, className)}>{children}</span>;
}
